import { useState, useEffect, useCallback } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { PublisherDetail } from 'types/publisher';
import { listPublishers } from '../api/marketplaceApi';

export interface UsePublishersOptions {
  mine?: boolean;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface UsePublishersResult {
  publishers: PublisherDetail[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePublishers(opts?: UsePublishersOptions): UsePublishersResult {
  const apiClients = useApiClientsOrNull();

  const [publishers, setPublishers] = useState<PublisherDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => setFetchTrigger((prev) => prev + 1), []);

  useEffect(() => {
    if (!apiClients) {
      setPublishers([]);
      setTotal(0);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    listPublishers(apiClients.schemaClient, opts ?? {})
      .then((res) => {
        if (!cancelled) {
          setPublishers(res.publishers);
          setTotal(res.total);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load publishers');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [apiClients, fetchTrigger, opts?.mine, opts?.status, opts?.limit, opts?.offset]);

  return { publishers, total, loading, error, refetch };
}
